(function (root) {
  'use strict';

  function fulfillment(product, variant) {
    if (!variant || variant.available !== true) return 'unavailable';
    if (variant.inventoryManagement == null || variant.inventoryManagement === '') return 'in_stock';
    if (!Number.isSafeInteger(variant.inventoryQuantity)) return 'unavailable';
    if (variant.inventoryQuantity > 0) return 'in_stock';
    if (variant.inventoryPolicy !== 'continue') return 'unavailable';
    return product.preOrder === true ? 'pre_order' : 'made_to_order';
  }

  function quantityOneAllowed(rule) {
    if (!rule || !Number.isInteger(rule.min) || rule.min < 1 || rule.min > 1 ||
        !Number.isInteger(rule.increment) || rule.increment < 1 ||
        (rule.max != null && (!Number.isInteger(rule.max) || rule.max < 1))) return false;
    return (1 - rule.min) % rule.increment === 0;
  }

  function selection(products, ids) {
    if (!Array.isArray(products) || ![2, 3].includes(products.length) || !Array.isArray(ids) || ids.length !== products.length) return null;
    if (new Set(products.map((product) => String(product?.id))).size !== products.length) return null;
    const items = [];
    let total = 0;
    for (let index = 0; index < products.length; index += 1) {
      const product = products[index];
      if (!product || !/^[1-9]\d*$/.test(String(product.id)) || product.purchasable !== true || !Array.isArray(product.variants)) return null;
      const matches = product.variants.filter((value) => String(value.id) === String(ids[index]));
      if (matches.length !== 1) return null;
      const variant = matches[0];
      if (!/^[1-9]\d*$/.test(String(variant.id)) || variant.available !== true ||
          variant.requiresSellingPlan !== false || !Number.isSafeInteger(variant.price) || variant.price < 0 ||
          !quantityOneAllowed(variant.quantityRule)) return null;
      const state = fulfillment(product, variant);
      if (state === 'unavailable' || items.some((item) => String(item.id) === String(variant.id))) return null;
      const item = { id: variant.id, quantity: 1 };
      if (state === 'made_to_order') item.properties = { Fulfillment: 'Made to Order' };
      items.push(item);
      total += variant.price;
    }
    return Number.isSafeInteger(total) ? { items, total } : null;
  }

  function mainFormSupported(elements) {
    return [...(elements || [])].every((field) => {
      if (field.disabled) return true;
      if (field.name === 'selling_plan' && field.value && (!['radio', 'checkbox'].includes(field.type) || field.checked)) return false;
      if (field.name?.startsWith('properties[') && field.name !== 'properties[Fulfillment]' && (field.required || field.value)) return false;
      if (field.type === 'checkbox' && (field.required || field.id?.startsWith('agree_condition-')) && !field.checked) return false;
      return true;
    });
  }

  function canonicalMetal(value) {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase().replace(/[−–—-]/g, ' ').replace(/\s+/g, ' ');
    if (/^(?:(?:14k|14kt|14 karat) )?gold(?: vermeil)?$/.test(normalized)) return 'gold';
    if (/^(?:silver|(?:925 |\.925 )?sterling silver|(?:bright )?rhodium (?:plated )?(?:925 )?sterling silver)$/.test(normalized)) return 'silver';
    return null;
  }

  function sizeKey(value) {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/^US\s*/i, '').replace(/\s+/g, ' ').toUpperCase();
  }

  function optionPlan(product) {
    if (!product || !Array.isArray(product.optionNames) || !product.optionNames.length || product.optionNames.length > 3 ||
        !Array.isArray(product.variants) || !product.variants.length || product.variants.length > 50) return null;
    const names = product.optionNames.map((value) => typeof value === 'string' ? value.trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ') : '');
    if (names.some((name) => !name) || new Set(names).size !== names.length) return null;
    if (product.variants.some((variant) => !Array.isArray(variant.options) || variant.options.length !== names.length || variant.options.some((value) => typeof value !== 'string' || !value.trim()))) return null;
    const sizes = names.flatMap((name, index) => /^(?:us )?(?:ring )?size(?: \(us\))?$/.test(name) ? [index] : []);
    if (sizes.length !== 1) return null;
    const sizeIndex = sizes[0];
    const metals = names.flatMap((name, index) => ['metal', 'finish'].includes(name) || (['color', 'colour'].includes(name) && product.variants.every((variant) => canonicalMetal(variant.options[index]))) ? [index] : []);
    if (metals.length > 1) return null;
    const metalIndex = metals[0] ?? -1;
    if (metalIndex >= 0 && product.variants.some((variant) => !canonicalMetal(variant.options[metalIndex]))) return null;
    const fixedMetal = metalIndex < 0 ? canonicalMetal(product.metalLabel) : null;
    if (metalIndex < 0 && !fixedMetal) return null;
    for (let index = 0; index < names.length; index += 1) {
      if (index !== sizeIndex && index !== metalIndex && new Set(product.variants.map((variant) => variant.options[index])).size !== 1) return null;
    }
    return { sizeIndex, metalIndex, fixedMetal };
  }

  function sizePlan(products, mainId, allowPartial = false) {
    if (!Array.isArray(products) || ![2, 3].includes(products.length) || new Set(products.map((product) => String(product?.id))).size !== products.length) return null;
    const axes = products.map(optionPlan);
    if (!axes[0] || (!allowPartial && axes.some((axis) => !axis))) return null;
    const mains = products[0].variants.filter((variant) => String(variant.id) === String(mainId));
    if (mains.length !== 1) return null;
    const mainVariant = mains[0];
    const metal = axes[0].metalIndex < 0 ? axes[0].fixedMetal : canonicalMetal(mainVariant.options[axes[0].metalIndex]);
    if (!metal) return null;
    const rows = products.map((product, index) => {
      const axis = axes[index];
      if (!axis) return null;
      const variantMetal = (variant) => axis.metalIndex < 0 ? axis.fixedMetal : canonicalMetal(variant.options[axis.metalIndex]);
      const variants = product.variants.filter((variant) => variantMetal(variant) === metal);
      if (!variants.length || new Set(variants.map((variant) => sizeKey(variant.options[axis.sizeIndex]))).size !== variants.length) return null;
      const metalLabel = axis.metalIndex < 0 ? product.metalLabel : variants[0].options[axis.metalIndex];
      return { variants, sizeIndex: axis.sizeIndex, metalLabel, safeFeaturedImage: canonicalMetal(product.metalLabel) === metal || product.variants.every((variant) => variantMetal(variant) === metal) };
    });
    if (!allowPartial && rows.some((row) => !row)) return null;
    return { metal, rows, mainSize: sizeKey(mainVariant.options[axes[0].sizeIndex]) };
  }

  function companionChoices(plan, products, previous = [], touched = [], sameMetal = true) {
    const count = Array.isArray(products) && [2, 3].includes(products.length) ? products.length : 0;
    if (!plan || !Array.isArray(plan.rows) || plan.rows.length !== count || !count) return Array(Math.max(0, count - 1)).fill('');
    return plan.rows.slice(1).map((row, index) => {
      if (!row) return '';
      const current = row.variants.find((variant) => String(variant.id) === String(previous[index]));
      if (sameMetal && touched[index] && current) return String(current.id);
      if (sameMetal && touched[index] && previous[index] === '') return '';
      const matches = row.variants.filter((variant) => sizeKey(variant.options[row.sizeIndex]) === plan.mainSize &&
        variant.available === true && variant.requiresSellingPlan === false && quantityOneAllowed(variant.quantityRule) && fulfillment(products[index + 1], variant) !== 'unavailable');
      return matches.length === 1 ? String(matches[0].id) : '';
    });
  }

  function previewImage(product, variant, row) {
    if (!row) return '';
    const allowedVariant = row.variants.find((value) => String(value.id) === String(variant?.id));
    return allowedVariant?.image || row.variants.find((value) => value.image)?.image || (row.safeFeaturedImage ? product.image : '') || '';
  }

  function previewTarget(variant, row) {
    if (!row) return null;
    const valid = row.variants.filter((value) => /^[1-9]\d*$/.test(String(value.id)));
    return valid.find((value) => String(value.id) === String(variant?.id)) || valid[0] || null;
  }

  function bundlerBundles(source = root) {
    // Read the installed App's saved rule; never activate a legacy metafield flag.
    try {
      const bundles = source.bndlr?.getBundles?.();
      return Array.isArray(bundles) ? bundles : null;
    } catch (_) { return null; }
  }

  function stackSubset(products, ids, included) {
    if (!Array.isArray(products) || ![2, 3].includes(products.length) || !Array.isArray(ids) || ids.length !== products.length) return null;
    if (included == null) included = products.slice(1).map(() => true);
    if (!Array.isArray(included) || included.length !== products.length - 1 || included.some((value) => typeof value !== 'boolean')) return null;
    const indexes = [0, ...included.flatMap((value, index) => value ? [index + 1] : [])];
    return { indexes, products: indexes.map((index) => products[index]), ids: indexes.map((index) => ids[index]) };
  }

  function validateOffer(bundles, products, ids, plan, configuredProducts = products) {
    const chosen = selection(products, ids);
    if (!chosen || !Array.isArray(bundles) || !plan || !Array.isArray(plan.rows) || plan.rows.length !== products.length ||
        plan.rows.some((row, index) => !Array.isArray(row?.variants) || !row.variants.some((variant) => String(variant.id) === String(ids[index])))) return null;
    if (!Array.isArray(configuredProducts) || ![2, 3].includes(configuredProducts.length)) return null;
    const poolIds = configuredProducts.map((product) => String(product?.id));
    const selectedIds = products.map((product) => String(product.id));
    if (poolIds.some((id) => !/^[1-9]\d*$/.test(id)) || new Set(poolIds).size !== poolIds.length ||
        selectedIds[0] !== poolIds[0] || selectedIds.some((id) => !poolIds.includes(id)) ||
        poolIds.filter((id) => selectedIds.includes(id)).join(',') !== selectedIds.join(',')) return null;
    const map = (value) => value && typeof value === 'object' && !Array.isArray(value);
    const empty = (value) => Array.isArray(value) && value.length === 0;
    const keys = new Set(('id name title description button_text discount_warning discount_type percentage_value fixed_amount_value fixed_price_value priority status product_level total_price_text minimum_requirements minimum_requirements_num minimum_requirements_n_max_products show_bundle bundle_image list_product_names mix_and_match_display free_shipping is_volume_bundle product_target_type volume_bundle_combine_quantites limit_for_customer_tags use_date_condition date_from date_to tags_additional_options is_standalone_product_bundle volume_bundle_cart_value_use_all_products version is_quantity_break quantity_break_subscription_integration quantity_break_subscription_integration_label quantity_break_show_variant_picker quantity_break_template quantity_break_display_variant_picker_below_widget apply_on_subscriptions is_frequently_purchased_bundle frequently_purchased_display_mode products required_products volume_discounts sections eligibility eligibilities translations').split(' '));
    const requiredFalse = ['use_date_condition', 'is_volume_bundle', 'is_quantity_break', 'is_standalone_product_bundle', 'apply_on_subscriptions'];
    const optionalFalse = ['free_shipping', 'volume_bundle_combine_quantites', 'volume_bundle_cart_value_use_all_products', 'quantity_break_subscription_integration'];
    let conflict = false;
    const candidates = bundles.flatMap((bundle) => {
      const memberIds = map(bundle?.products) ? Object.keys(bundle.products) : [];
      const samePool = memberIds.length === poolIds.length && poolIds.every((id) => memberIds.includes(id));
      const sameSelection = memberIds.length === selectedIds.length && selectedIds.every((id) => memberIds.includes(id));
      const related = bundle?.status === 'enabled' && (samePool || sameSelection);
      const reject = () => { if (related) conflict = true; return []; };
      if (bundle?.status !== 'enabled') return [];
      if (!samePool && !sameSelection) {
        const missingClassicMember = bundle.minimum_requirements === 'all_products' &&
          memberIds.some((id) => !selectedIds.includes(id));
        const missingRequiredMember = bundle.version === 2 && bundle.product_level === 'product' &&
          bundle.product_target_type === 'specific_products' && bundle.minimum_requirements === 'n_products' &&
          bundle.minimum_requirements_num === 2 && bundle.minimum_requirements_n_max_products === 2 &&
          bundle.mix_and_match_display === 'true' && requiredFalse.every((key) => bundle[key] === 'false') &&
          Object.keys(bundle).every((key) => keys.has(key)) && memberIds.some((id) =>
            !selectedIds.includes(id) && String(bundle.products[id]?.id) === id && bundle.products[id]?.required === 1);
        if (!missingClassicMember && !missingRequiredMember &&
            (memberIds.some((id) => selectedIds.includes(id)) || bundle.product_target_type !== 'specific_products')) conflict = true;
        return [];
      }
      const classic = bundle.minimum_requirements === 'all_products' && bundle.minimum_requirements_num === 1 &&
        bundle.minimum_requirements_n_max_products == null && bundle.mix_and_match_display === 'false' && bundle.is_frequently_purchased_bundle === 'false';
      const pair = samePool && poolIds.length === 3 && bundle.minimum_requirements === 'n_products' &&
        bundle.minimum_requirements_num === 2 && bundle.minimum_requirements_n_max_products === 2 && bundle.mix_and_match_display === 'true' &&
        (bundle.is_frequently_purchased_bundle === 'false' ||
          (bundle.is_frequently_purchased_bundle === 'true' && bundle.frequently_purchased_display_mode === 'standalone_widget'));
      if (!map(bundle) || Object.keys(bundle).some((key) => !keys.has(key)) || !/^[1-9]\d*$/.test(String(bundle.id)) ||
          bundle.version !== 2 || bundle.status !== 'enabled' || bundle.product_level !== 'product' ||
          bundle.discount_type !== 'percentage' || (!classic && !pair) ||
          bundle.product_target_type !== 'specific_products' || bundle.eligibility !== 'all' ||
          requiredFalse.some((key) => bundle[key] !== 'false') ||
          optionalFalse.some((key) => bundle[key] != null && bundle[key] !== 'false') ||
          ['limit_for_customer_tags', 'eligibilities', 'required_products', 'volume_discounts', 'sections'].some((key) => !empty(bundle[key])) ||
          (bundle.tags_additional_options != null && bundle.tags_additional_options !== '') ||
          !map(bundle.products)) return reject();
      const rawPercent = bundle.percentage_value;
      if (!['string', 'number'].includes(typeof rawPercent) || !/^\d+(?:\.\d+)?$/.test(String(rawPercent))) return reject();
      const percent = Number(rawPercent);
      if (!Number.isFinite(percent) || percent <= 0 || percent > 100) return reject();
      for (const id of memberIds) {
        const member = bundle.products[id];
        const required = pair && id === poolIds[0] ? 1 : 0;
        if (!map(member) || String(member.id) !== id || member.quantity !== 1 || member.status !== 'active' ||
            member.required !== required || member.selling_plan_id !== '' ||
            (member.discount_amount != null && member.discount_amount !== 0) || !map(member.variants)) return reject();
      }
      if (classic && !sameSelection) return [];
      let total = 0;
      for (let index = 0; index < products.length; index += 1) {
        const product = products[index];
        const member = bundle.products[String(product.id)];
        const required = pair && index === 0 ? 1 : 0;
        const eligible = member.variants[String(ids[index])];
        if (!map(eligible) || String(eligible.id) !== String(ids[index]) || eligible.was_deleted !== 0 || eligible.selling_plan_id !== '' ||
            (pair ? eligible.quantity !== 1 || eligible.required !== required :
              (eligible.quantity != null && eligible.quantity !== 1) || (eligible.required != null && eligible.required !== 0)) ||
            (eligible.discount_amount != null && eligible.discount_amount !== 0)) return reject();
        const variant = product.variants.find((value) => String(value.id) === String(ids[index]));
        // Bundler rounds each line's percentage reduction in the current currency.
        const discount = Math.round(variant.price * (percent / 100));
        if (!Number.isSafeInteger(discount) || discount < 0 || discount > variant.price) return reject();
        total += variant.price - discount;
      }
      return Number.isSafeInteger(total) && total < chosen.total
        ? [{ pair, priority: bundle.priority, offer: { originalTotal: chosen.total, total, percent, bundleId: bundle.id } }] : reject();
    });
    if (conflict) return null;
    const offers = candidates.filter((candidate) => !candidate.pair || products.length === 2);
    if (offers.length !== 1) return null;
    const winner = offers[0];
    const pairs = candidates.filter((candidate) => candidate.pair);
    if (products.length === 3 && pairs.length) {
      if (pairs.length !== 1 || !Number.isSafeInteger(winner.priority) || winner.priority < 1 ||
          !Number.isSafeInteger(pairs[0].priority) || pairs[0].priority < 1 || winner.priority >= pairs[0].priority ||
          winner.offer.percent <= pairs[0].offer.percent) return null;
    }
    return winner.offer;
  }

  const api = { fulfillment, quantityOneAllowed, selection, mainFormSupported, canonicalMetal, sizeKey, optionPlan, sizePlan, companionChoices, previewImage, previewTarget, bundlerBundles, validateOffer, stackSubset };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.customElements || !root.HTMLElement || root.customElements.get('alvaura-complete-stack')) return;

  class AlvauraCompleteStack extends root.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('[data-stack-add]');
      this.error = this.querySelector('[data-stack-error]');
      this.cartLink = this.querySelector('[data-stack-cart]');
      this.total = this.querySelector('[data-stack-total]');
      this.rows = [...this.querySelectorAll('[data-stack-item]')];
      this.includes = this.rows.slice(1).map((row) => row.querySelector('[data-stack-include]'));
      this.selects = [...this.querySelectorAll('[data-stack-variant]')];
      this.productInfo = this.closest('product-info');
      try {
        this.products = [...this.querySelectorAll('[data-stack-product]')].map((node) => JSON.parse(node.textContent));
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency, currencyDisplay: 'code' });
      } catch (_) { return; }
      this.fixedOptions = this.dataset.fixedOptions === 'true';
      if (this.fixedOptions) this.initializeLook();
      this.onChange = (event) => {
        if ((this.busy || this.uncertain) && event?.target?.matches?.('[data-stack-include]')) return;
        if (this.fixedOptions) {
          const index = this.selects.indexOf(event?.target);
          if (index >= 0) this.touched[index] = true;
        }
        root.requestAnimationFrame(() => { if (this.initialized) this.update(); });
      };
      this.onAdd = (event) => { if (!(event?.detail > 1)) this.add(); };
      this.addEventListener('change', this.onChange);
      this.productInfo?.addEventListener('change', this.onChange);
      this.button?.addEventListener('click', this.onAdd);
      this.observer = new MutationObserver(() => {
        const id = this.mainId();
        if (id !== this.lastMainId) this.update();
      });
      if (this.productInfo) this.observer.observe(this.productInfo, { attributes: true, attributeFilter: ['value', 'disabled', 'data-state'], childList: true, subtree: true });
      const subscribeTo = root.subscribe || (typeof subscribe === 'function' ? subscribe : null);
      const events = root.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
      if (subscribeTo && events?.variantChange) {
        this.unsubscribe = subscribeTo(events.variantChange, ({ data }) => {
          if (String(data?.sectionId) === String(this.productInfo?.dataset.originalSection || this.dataset.sectionId)) this.onChange();
        });
      }
      this.onVariantUpdate = (event) => {
        if ([this.productInfo?.id, this.dataset.sectionId].includes(event?.detail?.sourceId)) this.onChange();
      };
      document.addEventListener('variant:update', this.onVariantUpdate);
      this.onBundlerLoaded = () => { if (this.initialized) this.update(); };
      if (this.fixedOptions) document.addEventListener('bundler:loaded', this.onBundlerLoaded);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.productInfo?.removeEventListener('change', this.onChange);
      this.button?.removeEventListener('click', this.onAdd);
      this.observer?.disconnect();
      this.unsubscribe?.();
      document.removeEventListener('variant:update', this.onVariantUpdate);
      document.removeEventListener('bundler:loaded', this.onBundlerLoaded);
      if (this.fixedOptions) {
        this.removeEventListener('click', this.onLookClick);
        this.dialog?.removeEventListener('close', this.onDialogClose);
        this.dialog?.removeEventListener('cancel', this.onDialogCancel);
        if (this.dialog?.open) this.dialog.close();
        this.restorePreview();
        this.resetVideo();
        this.video?.removeEventListener('error', this.onVideoError);
      }
      this.initialized = false;
    }

    mainForm() {
      return this.productInfo?.querySelector(`form[id="product-form-${this.dataset.sectionId}"]`) || null;
    }

    mainId() {
      const form = this.mainForm();
      const input = form?.querySelector('input[name="id"].product-variant-id');
      if (!input || input.disabled || !mainFormSupported(form.elements)) return '';
      const availability = this.productInfo?.querySelector(`alvaura-availability[data-section-id="${this.dataset.sectionId}"]`);
      if (availability && (!availability.currentRecord || String(availability.currentRecord.variantId) !== String(input.value) || ['unavailable', 'pending', 'sold_out'].includes(availability.dataset.state))) return '';
      return input.value || '';
    }

    update() {
      if (!this.products || !this.money) return;
      if (this.fixedOptions) this.updateSizes();
      const ids = [this.mainId(), ...this.selects.map((select) => select.value)];
      this.lastMainId = ids[0];
      const included = this.rows.slice(1).map((_, index) => this.includes?.[index]?.checked !== false);
      this.activeStack = stackSubset(this.products, ids, included);
      this.activePlan = this.plan && this.activeStack && this.activeStack.indexes.every((index) => this.plan.rows[index])
        ? { ...this.plan, rows: this.activeStack.indexes.map((index) => this.plan.rows[index]) } : null;
      const empty = this.activeStack?.indexes.length === 1;
      this.rows.forEach((row, index) => {
        const active = index === 0 || included[index - 1];
        row.dataset.stackSelected = String(active);
        const include = this.includes?.[index - 1];
        if (include) include.disabled = Boolean(this.busy || this.uncertain);
        if (this.fixedOptions && index > 0 && this.selects[index - 1]) this.selects[index - 1].disabled = !active || !this.plan?.rows[index] || Boolean(this.busy || this.uncertain);
        const product = this.products[index];
        const variant = product?.variants?.find((value) => String(value.id) === String(ids[index]));
        const state = fulfillment(product, variant);
        const stateLabel = { in_stock: this.dataset.stockLabel, pre_order: this.dataset.preorderLabel, made_to_order: this.dataset.mtoLabel, unavailable: this.dataset.unavailableLabel }[state];
        row.querySelector('[data-stack-price]').textContent = variant && Number.isSafeInteger(variant.price) ? this.money.format(variant.price / 100) : '';
        row.querySelector('[data-stack-state]').textContent = variant ? stateLabel : '';
        if (index === 0) row.querySelector('[data-stack-main-choice]').textContent = variant ? variant.title : this.dataset.chooseLabel;
      });
      this.selected = this.activeStack ? selection(this.activeStack.products, this.activeStack.ids) : null;
      if (this.fixedOptions && !this.activePlan) this.selected = null;
      this.dataset.stackInvalid = String(!this.selected);
      const invalid = this.querySelector('[data-stack-plan-invalid]');
      if (invalid) invalid.hidden = empty || Boolean(this.activePlan);
      this.total.textContent = this.selected ? this.money.format(this.selected.total / 100) : empty ? this.dataset.pickCompanionLabel || this.dataset.chooseLabel : this.dataset.chooseLabel;
      if (this.fixedOptions) this.updateLook(ids);
      this.button.disabled = !this.selected || Boolean(this.busy) || Boolean(this.uncertain);
    }

    initializeLook() {
      this.touched = Array(this.selects.length).fill(false);
      this.dialog = this.querySelector('[data-stack-dialog]');
      this.onDialogClose = () => this.restorePreview();
      this.onDialogCancel = (event) => { event.preventDefault(); this.dialog.close(); };
      this.dialog?.addEventListener('close', this.onDialogClose);
      this.dialog?.addEventListener('cancel', this.onDialogCancel);
      this.initializeMedia();
      this.onLookClick = (event) => {
        const button = event.target.closest?.('button');
        if (!button || !this.contains(button)) return;
        if (button.matches('[data-stack-sizes-toggle]')) {
          const open = this.dataset.sizesOpen !== 'true';
          this.dataset.sizesOpen = String(open);
          button.setAttribute('aria-expanded', String(open));
          if (open) this.selects.find((select) => !select.disabled)?.focus({ preventScroll: true });
        } else if (button.matches('[data-stack-close]')) this.dialog?.close();
        else if (button.matches('[data-stack-video-play]')) this.playVideo();
        else if (button.matches('[data-stack-preview], [data-stack-look-preview]')) this.openPreview(button);
      };
      this.addEventListener('click', this.onLookClick);
    }

    updateSizes() {
      const previousMetal = this.lastPlanMetal;
      const plan = sizePlan(this.products, this.mainId(), true);
      this.plan = plan;
      const invalid = this.querySelector('[data-stack-plan-invalid]');
      if (invalid) invalid.hidden = Boolean(plan);
      if (!plan) {
        this.selects.forEach((select) => { select.disabled = true; });
        return;
      }
      const sameMetal = previousMetal == null || previousMetal === plan.metal;
      if (!sameMetal) this.touched = Array(this.selects.length).fill(false);
      const ids = companionChoices(plan, this.products, this.selects.map((select) => select.value), this.touched, sameMetal);
      this.lastPlanMetal = plan.metal;
      this.selects.forEach((select, index) => {
        const row = plan.rows[index + 1];
        if (!row) { select.value = ''; select.disabled = true; return; }
        const key = JSON.stringify([plan.metal, row.variants.map((variant) => [variant.id, variant.available, variant.price, variant.options[row.sizeIndex]])]);
        if (select.dataset.stackOptionsKey !== key) {
          const blank = document.createElement('option');
          blank.value = '';
          blank.textContent = this.dataset.chooseSizeLabel || this.dataset.chooseLabel;
          const options = [blank, ...row.variants.map((variant) => {
            const option = document.createElement('option');
            option.value = String(variant.id);
            option.textContent = `${this.dataset.sizePrefix || 'US'} ${sizeKey(variant.options[row.sizeIndex])}`;
            option.disabled = variant.available !== true || variant.requiresSellingPlan !== false || !quantityOneAllowed(variant.quantityRule) || fulfillment(this.products[index + 1], variant) === 'unavailable';
            if (option.disabled) option.textContent += ` — ${this.dataset.unavailableLabel}`;
            return option;
          })];
          select.replaceChildren(...options);
          select.dataset.stackOptionsKey = key;
        }
        select.value = ids[index];
        select.disabled = Boolean(this.busy);
      });
    }

    updateLook(ids) {
      this.dataset.lookMismatch = String(!this.plan || canonicalMetal(this.dataset.lookMetal) !== this.plan.metal);
      if (this.dataset.lookMismatch === 'true') this.resetVideo();
      const sizes = this.querySelector('[data-stack-sizes]');
      if (sizes) {
        const indexes = this.activeStack?.indexes || this.products.map((_, index) => index);
        const values = indexes.map((index) => {
          const product = this.products[index];
          const variant = product.variants.find((value) => String(value.id) === String(ids[index]));
          return variant && this.plan?.rows[index] ? sizeKey(variant.options[this.plan.rows[index].sizeIndex]) : '—';
        });
        const ringsLabel = indexes.length === 1 && this.dataset.singleRingLabel ? this.dataset.singleRingLabel : (this.dataset.ringsLabel || `${indexes.length} rings`).replace('__COUNT__', String(indexes.length));
        sizes.textContent = `${ringsLabel} · ${this.dataset.sizePrefix || 'US'} ${values.join(' / ')}`;
      }
      const caption = this.querySelector('[data-stack-media-caption]');
      if (caption) caption.hidden = this.dataset.lookMismatch === 'true' || this.activeStack?.products.length === this.products.length;
      this.rows.forEach((row, index) => {
        const metal = row.querySelector('[data-stack-metal]');
        if (metal) metal.textContent = this.plan?.rows[index]?.metalLabel || '';
        const variant = this.products[index].variants.find((value) => String(value.id) === String(ids[index]));
        const image = row.querySelector('[data-stack-image] img');
        const src = previewImage(this.products[index], variant, this.plan?.rows[index]);
        const preview = row.querySelector('[data-stack-preview]');
        if (preview) preview.disabled = !src;
        if (image) {
          image.hidden = !src;
          if (src) { image.src = src; image.removeAttribute('srcset'); }
        }
      });
      const offer = this.selected ? validateOffer(bundlerBundles(), this.activeStack.products, this.activeStack.ids, this.activePlan, this.products) : null;
      const original = this.querySelector('[data-stack-original]');
      const saving = this.querySelector('[data-stack-saving]');
      const note = this.querySelector('[data-stack-offer-note]');
      if (original) { original.hidden = !offer; original.textContent = offer ? this.money.format(offer.originalTotal / 100) : ''; }
      if (saving) { saving.hidden = !offer; saving.textContent = offer ? (this.dataset.savingLabel || 'Save [percent]%').replace('[percent]', String(offer.percent)) : ''; }
      if (note) note.hidden = !offer;
      if (offer) this.total.textContent = this.money.format(offer.total / 100);
    }

    initializeMedia() {
      this.media = this.querySelector('[data-stack-media]');
      this.video = this.querySelector('[data-stack-video]');
      if (!this.media || !this.video || typeof this.video.play !== 'function' || typeof this.video.pause !== 'function') {
        this.video = null;
        return;
      }
      this.videoCover = this.querySelector('[data-stack-video-cover]');
      this.videoPlay = this.querySelector('[data-stack-video-play]');
      this.imageFallback = this.querySelector('[data-stack-image-fallback]');
      this.video.autoplay = false;
      this.video.removeAttribute('autoplay');
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.controls = true;
      this.onVideoError = () => this.failVideo();
      this.video.addEventListener('error', this.onVideoError);
      this.resetVideo();
      if (this.video.error) this.failVideo();
    }

    playVideo() {
      if (!this.video || !this.media || this.dataset.lookMismatch === 'true' || this.media.dataset.videoFailed === 'true' || this.media.dataset.videoPlaying === 'true') return;
      this.video.muted = true;
      this.video.hidden = false;
      this.video.focus?.({ preventScroll: true });
      if (this.videoCover) this.videoCover.hidden = true;
      if (this.videoPlay) this.videoPlay.hidden = true;
      this.media.dataset.videoPlaying = 'true';
      this.playbackAttempt = (this.playbackAttempt || 0) + 1;
      const playback = this.playbackAttempt;
      try {
        Promise.resolve(this.video.play()).catch((error) => {
          if (playback !== this.playbackAttempt || this.media.dataset.videoFailed === 'true') return;
          if (error?.name === 'NotSupportedError' || this.video.error) this.failVideo();
          else this.resetVideo();
        });
      } catch (error) {
        if (error?.name === 'NotSupportedError' || this.video.error) this.failVideo();
        else this.resetVideo();
      }
    }

    resetVideo() {
      if (!this.video || !this.media) return;
      this.playbackAttempt = (this.playbackAttempt || 0) + 1;
      this.video.pause();
      try { this.video.currentTime = 0; } catch (_) {}
      this.video.hidden = true;
      this.media.dataset.videoPlaying = 'false';
      if (this.media.dataset.videoFailed !== 'true') {
        if (this.videoCover) this.videoCover.hidden = false;
        if (this.videoPlay) this.videoPlay.hidden = false;
        if (document.activeElement === this.video) this.videoPlay?.focus?.({ preventScroll: true });
      }
    }

    failVideo() {
      if (!this.video || !this.media) return;
      this.media.dataset.videoFailed = 'true';
      this.resetVideo();
      if (this.videoCover) this.videoCover.hidden = true;
      if (this.videoPlay) this.videoPlay.hidden = true;
      if (this.imageFallback) this.imageFallback.hidden = false;
      else {
        if (this.videoCover) this.videoCover.hidden = false;
      }
    }

    openPreview(button) {
      if (button.disabled || !this.dialog || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      const row = button.closest('[data-stack-item]');
      const index = this.rows.indexOf(row);
      if (index < 0 && this.dataset.lookMismatch === 'true') return;
      const template = index >= 0 ? row.querySelector('[data-stack-preview-content]') : this.querySelector('[data-stack-look-content]');
      const content = this.dialog.querySelector('[data-stack-dialog-content]');
      if (!template?.content || !content) return;
      const fragment = template.content.cloneNode(true);
      if (index >= 0) {
        const variantId = index === 0 ? this.mainId() : this.selects[index - 1].value;
        const variant = this.products[index].variants.find((value) => String(value.id) === String(variantId));
        const metal = fragment.querySelector('[data-stack-preview-metal]');
        if (metal) metal.textContent = this.plan?.rows[index]?.metalLabel || '';
        const image = fragment.querySelector('img');
        const src = previewImage(this.products[index], variant, this.plan?.rows[index]);
        if (!src || !image) return;
        if (image) {
          image.hidden = !src;
          if (src) { image.src = src; image.removeAttribute('srcset'); }
        }
        const details = fragment.querySelector('[data-stack-preview-link]');
        const target = previewTarget(variant, this.plan?.rows[index]);
        if (details && target) {
          const url = new URL(this.products[index].url, root.location.href);
          if (url.origin === root.location.origin || url.origin === new URL(root.location.href).origin) {
            url.searchParams.set('variant', String(target.id));
            details.href = url.pathname + url.search;
          }
        }
      }
      content.replaceChildren(fragment);
      this.previewState = { button, x: root.scrollX, y: root.scrollY, overflow: document.body.style.overflow };
      document.body.style.overflow = 'hidden';
      try { this.dialog.showModal(); }
      catch (_) { this.restorePreview(); return; }
      this.dialog.querySelector('[data-stack-close]')?.focus({ preventScroll: true });
    }

    restorePreview() {
      if (!this.previewState) return;
      const state = this.previewState;
      this.previewState = null;
      document.body.style.overflow = state.overflow;
      if (state.button.isConnected) state.button.focus({ preventScroll: true });
      root.scrollTo(state.x, state.y);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.cartLink.hidden = false;
    }

    async add() {
      this.update();
      if (!this.selected || this.busy || this.uncertain) return;
      const chosen = this.selected;
      let endpoint;
      try { endpoint = new URL(this.dataset.addUrl, root.location.href); }
      catch (_) { return; }
      if (endpoint.origin !== new URL(root.location.href).origin || !/\/cart\/add(?:\.js)?$/.test(endpoint.pathname)) return;
      this.busy = true;
      this.button.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      this.update();
      const drawer = document.querySelector('cart-drawer');
      const payload = { items: chosen.items };
      if (drawer?.getSectionsToRender) {
        payload.sections = drawer.getSectionsToRender().map((section) => section.id);
        payload.sections_url = root.location.pathname;
        const preview = new URL(root.location.href).searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) payload.sections_url += `?preview_theme_id=${preview}`;
        drawer.setActiveElement?.(this.button);
      }
      let added = false;
      try {
        const response = await fetch(endpoint.toString(), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.uncertain = true;
          this.showError(this.dataset.errorLabel);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartError, { source: 'alvaura-complete-stack', errors: data.errors || data.description });
          return;
        }
        if (!Array.isArray(data.items) || !chosen.items.every((item) => data.items.some((returned) => String(returned.variant_id || returned.id) === String(item.id)))) throw new Error('Unconfirmed set');
        added = true;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'alvaura-complete-stack', cartData: data });
        if (drawer?.renderContents) {
          if (!payload.sections?.every((id) => typeof data.sections?.[id] === 'string')) throw new Error('Incomplete cart sections');
          await drawer.renderContents(data);
        }
        else root.location.assign(this.dataset.cartUrl);
      } catch (_) {
        this.uncertain = true;
        this.showError(added ? this.dataset.renderErrorLabel : this.dataset.uncertainLabel);
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
        this.update();
      }
    }
  }
  root.customElements.define('alvaura-complete-stack', AlvauraCompleteStack);
})(typeof window === 'undefined' ? globalThis : window);
